import { Injectable } from '@angular/core';

import { ApiService } from 'src/app/services/api/api.service';

export interface FundInactivationPreviewRegister {
  nome: string;
  documento: string;
  tipo: 'PF' | 'PJ' | null;
}

export interface FundInactivationPreviewApiRegister {
  id?: string | null;
  name?: string;
  document_number?: string;
  type?: 'PF' | 'PJ' | null;
  role?: string | null;
}

export interface FundInactivationPreviewResponse {
  fund?: {
    id: string;
    name: string;
    document_number: string;
  };
  summary?: string;
  inactivatable_registers?: FundInactivationPreviewApiRegister[];
  protected_registers?: FundInactivationPreviewApiRegister[];
  inactivatable_shareholders?: FundInactivationPreviewApiRegister[];
  inactivatable_assignors?: FundInactivationPreviewApiRegister[];
  protected_shareholders?: FundInactivationPreviewApiRegister[];
  protected_assignors?: FundInactivationPreviewApiRegister[];
  fund_will_be_inactivated?: boolean;
  inactivatable_registers_count?: number;
  protected_registers_count?: number;
  inactivatable_shareholders_count?: number;
  inactivatable_assignors_count?: number;
  expected_total_changed?: number;
}

export interface FundInactivationPreviewView {
  inactivatedAssignors: FundInactivationPreviewRegister[];
  inactivatedShareholders: FundInactivationPreviewRegister[];
  inactivatedAssignorsCount: number;
  inactivatedShareholdersCount: number;
}

function mapPreviewRegister(
  register: FundInactivationPreviewApiRegister
): FundInactivationPreviewRegister {
  return {
    nome: register.name?.trim() || '-',
    documento: register.document_number?.trim() || '-',
    tipo: register.type ?? null,
  };
}

export function mapInactivationPreview(
  response: FundInactivationPreviewResponse
): FundInactivationPreviewView {
  const assignors = (response.inactivatable_assignors ?? []).map(
    mapPreviewRegister
  );
  const shareholders = (response.inactivatable_shareholders ?? []).map(
    mapPreviewRegister
  );

  return {
    inactivatedAssignors: assignors,
    inactivatedShareholders: shareholders,
    inactivatedAssignorsCount:
      response.inactivatable_assignors_count ?? assignors.length,
    inactivatedShareholdersCount:
      response.inactivatable_shareholders_count ?? shareholders.length,
  };
}

export interface FundTransferAdministratorPayload {
  companyName: string;
  documentNumber: string;
  contactName: string;
  email: string;
  phoneDdi: string;
  phoneNumber: string;
  mobileDdi: string;
  mobileNumber: string;
}

export interface FundTransferPayload {
  direction: 'out' | 'in';
  transferDate: string;
  assemblyDate: string;
  administrator: FundTransferAdministratorPayload;
}

export interface FundReactivationTransferEntry {
  companyDocument: string;
  companyName: string;
  email: string;
  phoneNumber?: string;
  mobileNumber?: string;
}

export interface FundInactivationTransferEntry
  extends FundReactivationTransferEntry {
  transferDate: string;
  assemblyDate?: string | null;
}

export interface FundInactivationPayload {
  confirm: boolean;
  reason: string;
  comment: string;
  scheduled_for?: string;
  transfer?: {
    entry: FundInactivationTransferEntry;
  };
}

export interface FundReactivationPayload {
  reason: 'transfer' | 'other' | string;
  comment: string;
  transfer?: {
    entry: FundReactivationTransferEntry;
  };
}

export interface FundScheduledInactivationTransferPayload {
  comment?: string;
  transfer: {
    entry: FundInactivationTransferEntry;
  };
}

export interface FundStatusOperationResponse {
  detail?: string;
  confirmation_required?: boolean;
  preview?: FundInactivationPreviewResponse;
  total_changed?: number;
  details?: {
    fund_id?: string;
    fund_restored?: boolean;
    comment?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class FundOperationalStatusService {
  constructor(private api: ApiService) {}

  getInactivationPreview(
    fundId: string | number
  ): Promise<FundInactivationPreviewResponse> {
    return this.api.get<FundInactivationPreviewResponse>({
      route: `api/registration/fund/${fundId}/inactivation-preview/`,
      token: true,
    });
  }

  inactivateFund(
    fundId: string | number,
    payload: FundInactivationPayload
  ): Promise<FundStatusOperationResponse> {
    return this.api.post<FundStatusOperationResponse>({
      route: `api/registration/fund/${fundId}/inactivate/`,
      token: true,
      body: payload,
    });
  }

  updateScheduledInactivationTransfer(
    fundId: string | number,
    payload: FundScheduledInactivationTransferPayload
  ): Promise<FundStatusOperationResponse> {
    return this.api.patch<FundStatusOperationResponse>({
      route: `api/registration/fund/${fundId}/scheduled-inactivation/transfer/`,
      token: true,
      body: payload,
    });
  }

  confirmScheduledInactivation(
    fundId: string | number
  ): Promise<FundStatusOperationResponse> {
    return this.api.post<FundStatusOperationResponse>({
      route: `api/registration/fund/${fundId}/confirm-inactivation/`,
      token: true,
    });
  }

  cancelScheduledInactivation(
    fundId: string | number
  ): Promise<FundStatusOperationResponse> {
    return this.api.post<FundStatusOperationResponse>({
      route: `api/registration/fund/${fundId}/cancel-inactivation/`,
      token: true,
    });
  }

  reactivateFund(
    fundId: string | number,
    payload: FundReactivationPayload
  ): Promise<FundStatusOperationResponse> {
    return this.api.post<FundStatusOperationResponse>({
      route: `api/registration/fund/${fundId}/reactivate/`,
      token: true,
      body: payload,
    });
  }
}
